import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BACKEND_URL } from "@/constants/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, ChevronLeft, AlertCircle, Trash2, Lock } from "lucide-react";

const nativeSelect =
  "h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm " +
  "ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 text-foreground";

export default function SchoolSettings() {
  const { schoolId } = useParams();
  const { getToken } = useAuth();
  const navigate = useNavigate();

  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [name, setName] = useState("");
  const [sharingMode, setSharingMode] = useState("free");
  const [domainsInput, setDomainsInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to load school");
      }
      const data = await res.json();
      setSchool(data);
      setName(data.name);
      setSharingMode(data.sharingMode);
      setDomainsInput((data.allowedDomains || []).join(", "));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [schoolId, getToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    const allowedDomains = domainsInput
      .split(",")
      .map((d) => d.trim().toLowerCase())
      .filter(Boolean);

    setSaving(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: name.trim(), sharingMode, allowedDomains }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to save settings");
      }
      const updated = await res.json();
      setSchool(updated);
      toast.success("Settings saved.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (
      !confirm(
        `Delete "${school?.name}"? This cannot be undone. Classes will remain but will no longer be associated with this school.`
      )
    )
      return;
    setDeleting(true);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}api/schools/${schoolId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to delete school");
      }
      toast.success("School deleted.");
      navigate("/schools");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-content">
        <div className="h-8 w-40 bg-muted animate-pulse rounded mb-6" />
        <div className="h-64 bg-muted animate-pulse rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content">
        <div className="flex items-center gap-2 text-destructive text-sm rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <AlertCircle size={15} />
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      <Link
        to={`/schools/${schoolId}`}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6"
      >
        <ChevronLeft size={14} />
        {school?.name}
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
          <Settings size={18} className="text-primary" />
        </div>
        <div>
          <h1 className="dashboard-heading-sm">School Settings</h1>
          <p className="dashboard-subheading">
            Update the school name, sharing policy, and domain restrictions.
          </p>
        </div>
      </div>

      <div className="dashboard-content-narrow">
      {/* Non-admin lock banner */}
      {!school?.isAdmin && (
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 px-4 py-3.5 mb-6">
          <Lock size={15} className="text-muted-foreground mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">
              You are not permitted to change school settings
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Contact your school admin to update sharing settings, domain
              restrictions, or member permissions.
            </p>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-2xl shadow-sm">
        <form onSubmit={handleSave} className="px-6 py-6 space-y-5">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              School Name <span className="text-destructive/80 font-normal">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="h-10"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Class Sharing Mode</Label>
            <select
              value={sharingMode}
              onChange={(e) => setSharingMode(e.target.value)}
              className={nativeSelect}
            >
              <option value="free">Free - teachers share classes directly</option>
              <option value="restricted">
                Restricted - admin approves all share requests
              </option>
            </select>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {sharingMode === "free"
                ? "Teachers can share classes with each other without approval."
                : "Every share request must be approved by you before access is granted."}
            </p>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">
              Allowed Email Domains{" "}
              <span className="text-muted-foreground font-normal text-xs">(optional)</span>
            </Label>
            <Input
              placeholder="e.g. tdsb.on.ca, ocdsb.ca"
              value={domainsInput}
              onChange={(e) => setDomainsInput(e.target.value)}
              className="h-10"
            />
            <p className="text-xs text-muted-foreground">
              Comma-separated. Leave blank to allow any email domain.
            </p>
          </div>

          {school?.isAdmin && (
            <div className="flex gap-2 pt-1">
              <Button type="submit" disabled={saving || !name.trim()} className="flex-1">
                {saving ? "Saving..." : "Save Settings"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(`/schools/${schoolId}`)}
              >
                Cancel
              </Button>
            </div>
          )}
        </form>
      </div>

      {/* Danger zone - admin only */}
      {school?.isAdmin && (
      <div className="mt-8 bg-card border border-destructive/30 rounded-2xl px-6 py-5">
        <h2 className="text-sm font-semibold text-destructive mb-1">Danger Zone</h2>
        <p className="text-xs text-muted-foreground mb-4">
          Deleting a school is permanent. Members will lose access but existing class data
          will be preserved. This action cannot be undone.
        </p>
        <Button
          variant="outline"
          size="sm"
          className="gap-2 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
          onClick={handleDelete}
          disabled={deleting}
        >
          <Trash2 size={14} />
          {deleting ? "Deleting..." : "Delete School"}
        </Button>
      </div>
      )}
      </div>
    </div>
  );
}
